import { Fragment, useRef, useState, useId } from 'react';

import Popover from '@/components/elements/Popover';
import Icon from '@/components/icons/Icon';
import { isServer, classnames } from '@/helpers';
import { SelectedTimeFilterOptions } from '@/types/reviews';
import { _t } from '@/locale';
import { Dropdown } from '@/components/modules/dropdown';
import { DropdownButton } from '@/components/elements/forms/DropdownButton';
import { TIME_FILTER } from '@/constants/timeConstants';

export function ReviewsHeader({
  setTimeFilter,
  disabled,
  className,
  selectedTimeFilter,
}: {
  setTimeFilter?: (date: SelectedTimeFilterOptions) => void;
  disabled?: boolean;
  selectedTimeFilter?: SelectedTimeFilterOptions;
  className?: string;
}) {
  const supportsPopover = !isServer && HTMLElement.prototype.hasOwnProperty('popover');
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropDownButtonRef = useRef<HTMLButtonElement | null>(null);

  const contentId = `reviews-info-popover-${useId().replace(/:/g, '')}`;

  const togglePopover = (visible: boolean) => {
    setPopoverVisible(visible);
    if (supportsPopover) {
      const popover = document.getElementById(contentId) as
        | (HTMLElement & { showPopover: () => void; hidePopover: () => void })
        | null;
      if (visible) {
        popover?.showPopover();
      } else {
        popover?.hidePopover();
      }
    }
  };

  const translations = {
    [TIME_FILTER.WITHIN_ONE_YEAR]: _t('timeFilter.year'),
    [TIME_FILTER.FOREVER]: _t('timeFilter.all'),
  };

  const DROP_DOWN_OPTIONS = [
    { label: _t('timeFilter.year'), value: TIME_FILTER.WITHIN_ONE_YEAR },
    { label: _t('timeFilter.all'), value: TIME_FILTER.FOREVER },
  ];

  return (
    <Fragment>
      <header
        className={classnames('flex items-center justify-between', {
          [className]: !!className,
        })}>
        <div className="gap-sm flex items-center">
          <h2 className="text-h-m flex-1 font-bold">{_t('reviewsTitle')}</h2>
          <button
            {...{ popovertarget: contentId }}
            className={classnames(
              `trigger-${contentId} focus-visible:outline-black-900 rounded-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`,
              {
                'pointer-events-none': disabled,
              },
            )}
            onMouseEnter={() => togglePopover(true)}
            onMouseLeave={() => togglePopover(false)}
            onFocus={() => togglePopover(true)}
            onBlur={() => togglePopover(false)}>
            <Icon variant="info-circle" color="fg_link" style={{ width: '20px', height: '20px' }} />
          </button>
        </div>
        {setTimeFilter ? (
          <div className="ml-sm relative hidden sm:block">
            <DropdownButton
              buttonRef={dropDownButtonRef}
              isActive={dropdownVisible}
              toggleOpen={() => {
                setDropdownVisible(!dropdownVisible);
              }}
              className={classnames(
                'bg-surface_l2 p-xs text-m p-2! active:bg-surface_l3 hover:bg-surface_l4 relative rounded-lg',
                {
                  'pointer-events-none': disabled,
                  'text-fg_disabled': disabled,
                },
              )}>
              <span className="text-m whitespace-nowrap">{translations[selectedTimeFilter]}</span>
            </DropdownButton>
            {dropdownVisible ? (
              <Dropdown
                allowClick={true}
                className="right-0 top-[36px] m-0 min-w-[20rem] !pb-0 shadow"
                onClickOutside={(e) => {
                  if (dropDownButtonRef.current && dropDownButtonRef.current.contains(e.target as Node)) {
                    return;
                  }
                  setDropdownVisible(false);
                }}>
                <ul className="space-y-sm px-md pb-md">
                  {DROP_DOWN_OPTIONS.map((option) => (
                    <li
                      key={option.value}
                      role={'checkbox'}
                      aria-checked={selectedTimeFilter === option.value}
                      className={classnames(
                        'px-sm py-xs text-m cursor-pointer rounded-sm',
                        selectedTimeFilter === option.value ? 'bg-surface_l3' : '',
                      )}
                      onClick={() => {
                        if (selectedTimeFilter === option.value) {
                          setDropdownVisible(false);
                          return;
                        }
                        setDropdownVisible(false);
                        setTimeFilter(option.value as SelectedTimeFilterOptions);
                      }}>
                      {option.label}
                    </li>
                  ))}
                </ul>
              </Dropdown>
            ) : null}
          </div>
        ) : null}
      </header>
      {(supportsPopover || (!supportsPopover && popoverVisible)) && (
        <Popover id={contentId} className={`${!supportsPopover ? 'z-[100] mt-2' : 'mt-2'}`} align={'right'}>
          <div className="space-y-md bg-bg_inverse p-lg w-full max-w-[400px] rounded-lg">
            <p className="text-fg_secondary_inverse" id="popover-content">
              {_t('leaveReviewPopover.body')}
            </p>
          </div>
        </Popover>
      )}
    </Fragment>
  );
}
